import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionTier } from "@/lib/stripe";

interface Profile {
  id: string;
  user_id: string;
  email: string | null;
  full_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  subscription_tier: SubscriptionTier;
  stripe_customer_id: string | null;
  subscription_status: string | null;
  subscription_current_period_end: string | null;
  beta_access: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  displayName: string;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  syncSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const syncInFlight = useRef(false);
  const fetchSeq = useRef(0); // prevents stale profile overwrites

  const displayName = (() => {
    if (profile?.display_name) return profile.display_name;
    if (profile?.full_name) return profile.full_name;
    const email = profile?.email || user?.email;
    if (email) {
      const prefix = email.split("@")[0].replace(/[._-]/g, " ");
      return prefix.charAt(0).toUpperCase() + prefix.slice(1).split(" ")[0];
    }
    return "Gardener";
  })();

  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("[AuthContext] Error fetching profile:", error);
        return null;
      }
      if (!data) {
        console.warn("[AuthContext] No profile found for user:", userId);
      }
      return data as Profile | null;
    } catch (error) {
      console.error("[AuthContext] Error fetching profile:", error);
      return null;
    }
  }, []);

  /**
   * Safely set profile only if this fetch is still the latest one.
   * Prevents older fetches from overwriting fresher data.
   */
  const safeSetProfile = useCallback((seq: number, data: Profile | null) => {
    if (seq === fetchSeq.current) {
      setProfile(data);
    }
  }, []);

  const syncSubscription = useCallback(async () => {
    if (!session || !user || syncInFlight.current) return;
    syncInFlight.current = true;

    try {
      setProfileLoading(true);
      const { error } = await supabase.functions.invoke("check-subscription");
      if (error) {
        console.error("[AuthContext] Error syncing subscription:", error);
      }
      // Re-read profile after sync
      const seq = ++fetchSeq.current;
      const freshProfile = await fetchProfile(user.id);
      safeSetProfile(seq, freshProfile);
    } catch (error) {
      console.error("[AuthContext] Error syncing subscription:", error);
    } finally {
      syncInFlight.current = false;
      setProfileLoading(false);
    }
  }, [session, user, fetchProfile, safeSetProfile]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    setProfileLoading(true);
    try {
      const seq = ++fetchSeq.current;
      const profileData = await fetchProfile(user.id);
      safeSetProfile(seq, profileData);
    } finally {
      setProfileLoading(false);
    }
  }, [user, fetchProfile, safeSetProfile]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          setTimeout(() => {
            const seq = ++fetchSeq.current;
            fetchProfile(newSession.user.id).then((p) => safeSetProfile(seq, p));
          }, 0);
        } else {
          fetchSeq.current++;
          setProfile(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);

      if (existingSession?.user) {
        const seq = ++fetchSeq.current;
        fetchProfile(existingSession.user.id).then((profileData) => {
          safeSetProfile(seq, profileData);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, safeSetProfile]);

  // Sync subscription on login (once profile is loaded)
  useEffect(() => {
    if (session && profile) {
      syncSubscription();
    }
  }, [session?.access_token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic sync every 60s
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(syncSubscription, 60000);
    return () => clearInterval(interval);
  }, [session, syncSubscription]);

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: { full_name: fullName || "" },
        },
      });
      return { error: error ?? null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error ?? null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    fetchSeq.current++;
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    profileLoading,
    displayName,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    syncSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
